import type * as SentryModule from '@sentry/react';

/**
 * Thin front for the Sentry SDK, which is loaded after first paint (see initAnalytics in analytics.ts).
 *
 * App code imports this module instead of `@sentry/react`, so the SDK (about 118 kB gzip) stays out of the
 * entry chunk. Calls made before the SDK has loaded are queued and replayed once it is initialised. Local
 * builds never load the SDK: calls are dropped, as they were when Sentry.init was skipped.
 */
type Sentry = typeof SentryModule;
type Call = (sentry: Sentry) => void;

/** Enough for a burst of start-up errors; later calls are dropped until the SDK is ready. */
export const MAX_QUEUED_CALLS = 50;

let sdk: Sentry | null = null;
let disabled = false;
let queue: Call[] = [];

const run = (call: Call) => {
  if (sdk) {
    call(sdk);
    return;
  }
  if (!disabled && queue.length < MAX_QUEUED_CALLS) {
    queue.push(call);
  }
};

export const captureException = (...args: Parameters<Sentry['captureException']>) => {
  run(sentry => sentry.captureException(...args));
};

export const captureMessage = (...args: Parameters<Sentry['captureMessage']>) => {
  run(sentry => sentry.captureMessage(...args));
};

export const setUser = (...args: Parameters<Sentry['setUser']>) => {
  run(sentry => sentry.setUser(...args));
};

/** Hands over the initialised SDK and replays the queued calls in order. */
export const attachSentry = (sentry: Sentry) => {
  sdk = sentry;
  const pending = queue;
  queue = [];
  pending.forEach(call => call(sentry));
};

/** Sentry will not be loaded (local build, or the SDK failed to load): drop queued and future calls. */
export const disableSentry = () => {
  disabled = true;
  queue = [];
};

/** Test helper: back to the state before initAnalytics ran. */
export const resetSentryForTests = () => {
  sdk = null;
  disabled = false;
  queue = [];
};
